import { doesConcretePathMatchPattern } from '@terra/shared-asset-processing';
import { normalizeHttpHost, normalizeHttpMethod } from '@terra/shared-utils';

export type HarReassociateRow = {
  id: string;
  method: string;
  url: string;
};

export type SharedHarIdentity =
  | { status: 'empty' }
  | { status: 'mixed' }
  | {
      status: 'ok';
      method: string;
      host: string;
      operation: string | undefined;
      segmentDepth: number;
    };

const parseHarUrl = (url: string): URL | undefined => {
  try {
    return new URL(url);
  } catch {
    return undefined;
  }
};

export const getHarUrlPath = (url: string): string =>
  parseHarUrl(url)?.pathname ?? url;

export const getHarUrlHost = (url: string): string =>
  parseHarUrl(url)?.host ?? '';

export const getHarUrlSegmentDepth = (url: string): number =>
  getHarUrlPath(url).split('/').filter(Boolean).length;

export const getHarOperationKey = (
  row: HarReassociateRow,
): string | undefined => {
  const parsed = parseHarUrl(row.url);

  if (!parsed) {
    return undefined;
  }

  const fromQuery =
    parsed.searchParams.get('operationName') ??
    parsed.searchParams.get('operation') ??
    undefined;

  if (fromQuery) {
    return fromQuery;
  }

  const pathname = parsed.pathname.toLowerCase();

  if (pathname.includes('graphql')) {
    return 'graphql';
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  const methodSegment = segments[segments.length - 1];
  const serviceSegment = segments[segments.length - 2];

  if (serviceSegment?.includes('.') && methodSegment) {
    return `${serviceSegment}/${methodSegment}`;
  }

  return undefined;
};

export const getSharedHarIdentity = (
  rows: readonly HarReassociateRow[],
): SharedHarIdentity => {
  const first = rows[0];

  if (!first) {
    return { status: 'empty' };
  }

  const method = normalizeHttpMethod(first.method);
  const host = normalizeHttpHost(getHarUrlHost(first.url));
  const operation = getHarOperationKey(first);
  const segmentDepth = getHarUrlSegmentDepth(first.url);
  const isMixed = rows.some(
    (row) =>
      normalizeHttpMethod(row.method) !== method ||
      normalizeHttpHost(getHarUrlHost(row.url)) !== host ||
      getHarOperationKey(row) !== operation ||
      getHarUrlSegmentDepth(row.url) !== segmentDepth,
  );

  if (isMixed) {
    return { status: 'mixed' };
  }

  return { status: 'ok', method, host, operation, segmentDepth };
};

export const defaultPathFromHarRows = (
  rows: readonly HarReassociateRow[],
): string => {
  const firstPath = rows[0] ? getHarUrlPath(rows[0].url) : '';

  return firstPath;
};

export const defaultPathsByHarId = (
  rows: readonly HarReassociateRow[],
): Record<string, string> =>
  Object.fromEntries(rows.map((row) => [row.id, getHarUrlPath(row.url)]));

export type HarCreateGroup = {
  id: string;
  rows: HarReassociateRow[];
};

export const harCreateGroupKey = (row: HarReassociateRow): string =>
  [
    normalizeHttpMethod(row.method),
    normalizeHttpHost(getHarUrlHost(row.url)),
    getHarUrlPath(row.url),
    getHarOperationKey(row) ?? '',
  ].join('\0');

export const groupHarRowsForCreate = (
  rows: readonly HarReassociateRow[],
): HarCreateGroup[] => {
  const order: string[] = [];
  const grouped = new Map<string, HarReassociateRow[]>();

  for (const row of rows) {
    const key = harCreateGroupKey(row);
    const existing = grouped.get(key);

    if (existing) {
      grouped.set(key, [...existing, row]);
      continue;
    }

    order.push(key);
    grouped.set(key, [row]);
  }

  return order.flatMap((key) => {
    const groupRows = grouped.get(key);
    const first = groupRows?.[0];

    if (!first || !groupRows) {
      return [];
    }

    return [{ id: first.id, rows: groupRows }];
  });
};

export const defaultPathsByCreateGroup = (
  groups: readonly HarCreateGroup[],
): Record<string, string> =>
  Object.fromEntries(
    groups.map((group) => [group.id, defaultPathFromHarRows(group.rows)]),
  );

export const existingTargetIdFromActiveCollision = ({
  suggestedEndpointId,
  sourceEndpointId,
}: {
  suggestedEndpointId: string;
  sourceEndpointId: string;
}): string | undefined =>
  suggestedEndpointId === sourceEndpointId ? undefined : suggestedEndpointId;

export const pathConsumesHarRows = ({
  path,
  rows,
}: {
  path: string;
  rows: readonly HarReassociateRow[];
}): boolean => {
  const pattern = path.trim();

  if (!pattern || rows.length === 0) {
    return false;
  }

  return rows.every((row) =>
    doesConcretePathMatchPattern(getHarUrlPath(row.url), pattern),
  );
};

export const canCreateFromHars = ({
  path,
  rows,
}: {
  path: string;
  rows: readonly HarReassociateRow[];
}): boolean => {
  if (getSharedHarIdentity(rows).status !== 'ok') {
    return false;
  }

  return pathConsumesHarRows({ path, rows });
};

export const canCreateFromHarRow = ({
  path,
  row,
}: {
  path: string;
  row: HarReassociateRow;
}): boolean =>
  canCreateFromHars({
    path,
    rows: [row],
  });

export const isAbsorbableReassociateEndpoint = ({
  rows,
  endpoint,
}: {
  rows: readonly HarReassociateRow[];
  endpoint: {
    method: string;
    host: string;
    path: string;
  };
}): boolean => {
  const identity = getSharedHarIdentity(rows);

  if (identity.status !== 'ok') {
    return false;
  }

  if (normalizeHttpMethod(endpoint.method) !== identity.method) {
    return false;
  }

  if (normalizeHttpHost(endpoint.host) !== identity.host) {
    return false;
  }

  const endpointDepth = endpoint.path.split('/').filter(Boolean).length;

  if (endpointDepth !== identity.segmentDepth) {
    return false;
  }

  return pathConsumesHarRows({ path: endpoint.path, rows });
};
